import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import type { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useCreateFlowInstanceMutation, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PrepareStepResult, resolveConnectorFlowErrorMessage } from './connectorFlowHelpers';

export type UseConnectorFlowRunnerArgs = {
  workspaceId: string;
  audienceId: string | undefined;
  connectorFlowId: string | undefined;
  connectorInstanceId: string | undefined;
  lang: string;
};

export type CreateInstanceResult = {
  success: boolean;
  message?: string;
};

export type UseConnectorFlowRunnerResult = {
  prepareResult: PrepareStepResult | null;
  prepareError: boolean;
  isPreparePending: boolean;
  reloadPrepare: () => void;
  isCreating: boolean;
  createInstance: (flowParams: FlowFormData, flowMappings: FlowFormData) => Promise<CreateInstanceResult>;
};

const prepareContextKey = (args: {
  workspaceId: string;
  audienceId: string | undefined;
  connectorFlowId: string | undefined;
  connectorInstanceId: string | undefined;
  lang: string;
}) => {
  if (!args.workspaceId || !args.audienceId || !args.connectorFlowId || !args.connectorInstanceId) {
    return null;
  }
  return `${args.workspaceId}|${args.audienceId}|${args.connectorFlowId}|${args.connectorInstanceId}|${args.lang}`;
};

export const useConnectorFlowRunner = ({
  workspaceId,
  audienceId,
  connectorFlowId,
  connectorInstanceId,
  lang,
}: UseConnectorFlowRunnerArgs): UseConnectorFlowRunnerResult => {
  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [createFlowInstance, { isLoading: isCreating }] = useCreateFlowInstanceMutation();

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareError, setPrepareError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const prepareRequestId = useRef(0);
  const preparedContextRef = useRef<string | null>(null);

  const canPrepare = Boolean(workspaceId && audienceId && connectorFlowId && connectorInstanceId);
  const contextKey = prepareContextKey({ workspaceId, audienceId, connectorFlowId, connectorInstanceId, lang });

  useEffect(() => {
    // Match old Workaround: do not clear prepareResult when canPrepare flickers false — that
    // unmounted MultiStepFlowForm and wiped live preview/form state.
    if (!canPrepare || !contextKey) {
      return undefined;
    }

    // Skip re-prepare when this exact context already succeeded (unless reloadPrepare cleared the ref).
    if (preparedContextRef.current === contextKey) {
      return undefined;
    }

    let cancelled = false;
    const requestId = ++prepareRequestId.current;
    setPrepareError(false);

    const run = async () => {
      try {
        const response = await prepareStep({
          workspaceId,
          flowId: connectorFlowId!,
          audienceId: audienceId!,
          stepId: 'step1',
          stepData: {},
          connectorInstanceId: connectorInstanceId!,
        }).unwrap();

        if (cancelled || requestId !== prepareRequestId.current) return;

        const translated = translateI18nValues(response, lang);
        setPrepareResult(translated?.configuration?.elements?.params as PrepareStepResult);
        preparedContextRef.current = contextKey;
      } catch {
        if (cancelled || requestId !== prepareRequestId.current) return;
        setPrepareError(true);
        setPrepareResult(null);
        preparedContextRef.current = null;
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [audienceId, canPrepare, connectorFlowId, connectorInstanceId, contextKey, lang, prepareStep, reloadToken, workspaceId]);

  const reloadPrepare = useCallback(() => {
    setPrepareError(false);
    setPrepareResult(null);
    preparedContextRef.current = null;
    setReloadToken((token) => token + 1);
  }, []);

  const createInstance = useCallback(
    async (flowParams: FlowFormData, flowMappings: FlowFormData): Promise<CreateInstanceResult> => {
      if (!audienceId || !connectorFlowId || !connectorInstanceId) {
        return { success: false, message: 'Missing connector flow context' };
      }

      const payload = buildFlowInstancePayload({
        workspaceId,
        connectorFlowId,
        connectorInstanceId,
        audienceId,
        flowParams,
        flowMappings,
      });

      try {
        await createFlowInstance(payload).unwrap();
        return { success: true };
      } catch (error) {
        return { success: false, message: resolveConnectorFlowErrorMessage(error, lang) };
      }
    },
    [audienceId, connectorFlowId, connectorInstanceId, createFlowInstance, lang, workspaceId]
  );

  const currentPrepareResult = !contextKey || preparedContextRef.current === contextKey ? prepareResult : null;
  const isPreparePending = canPrepare && !currentPrepareResult && !prepareError;

  return {
    prepareResult: currentPrepareResult,
    prepareError,
    isPreparePending,
    reloadPrepare,
    isCreating,
    createInstance,
  };
};
